<script setup>
import { computed, shallowRef } from "vue";

import { projects as projectsApi } from "~/api";
import GlAlert from "~/components/gitlab/GlAlert.vue";
import GlModal from "~/components/gitlab/GlModal.vue";
import { fetch } from "~/utils/api";

const emit = defineEmits(["close", "done"]);

const projectName = shallowRef("");
const accessToken = shallowRef("");
const submitting = shallowRef(false);
const alert = shallowRef(null);

const actionPrimary = computed(() => ({
  attributes: {
    category: "primary",
    disabled: submitting.value,
    variant: "confirm"
  },
  text: submitting.value ? "Adding…" : "Add project"
}));

const actionCancel = { text: "Cancel" };

async function onPrimary(event) {
  if (!projectName.value.trim()) {
    event.preventDefault();
    return;
  }

  event.preventDefault();
  submitting.value = true;
  alert.value = null;

  await fetch({
    alert,
    apiMethod: projectsApi.create,
    onError: () => {
      submitting.value = false;
    },
    onSuccess: (data) => {
      if (data.type === "success" || data.type === "warning") {
        emit("done", { message: data.message, type: data.type });
        emit("close");
      } else {
        alert.value = { message: data.message, type: data.type };
        submitting.value = false;
      }
    },
    requestArgs: {
      data: {
        access_token: accessToken.value.trim() || undefined,
        project_name: projectName.value.trim()
      }
    }
  });
}
</script>

<template>
  <GlModal
    modal-id="add-project-modal"
    title="Add new project"
    :visible="true"
    :action-primary="actionPrimary"
    :action-cancel="actionCancel"
    @primary="onPrimary"
    @canceled="emit('close')"
    @change="
      (v) => {
        if (!v) emit('close');
      }
    "
  >
    <GlAlert v-if="alert" :variant="alert.type" class="mb-4" @dismiss="alert = null">
      {{ alert.message }}
    </GlAlert>

    <div class="mb-4">
      <label for="project-name" class="mb-1.5 block text-sm font-medium text-[var(--gl-text-color-default)]">
        Project path
      </label>
      <input
        id="project-name"
        v-model="projectName"
        type="text"
        placeholder="Full project path (e.g. group/project)"
        required
        class="w-full rounded border border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)] px-3 py-2 text-sm text-[var(--gl-text-color-default)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)]"
      />
    </div>

    <div>
      <label for="access-token" class="mb-1.5 block text-sm font-medium text-[var(--gl-text-color-default)]">
        Access token
        <span class="font-normal text-[var(--gl-text-color-subtle)]">(optional)</span>
      </label>
      <input
        id="access-token"
        v-model="accessToken"
        type="password"
        placeholder="Personal or project access token"
        class="w-full rounded border border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)] px-3 py-2 text-sm text-[var(--gl-text-color-default)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)]"
      />
    </div>
  </GlModal>
</template>
